const LOADER_HTML = `
    <div class="tw-flex tw-min-h-[200px] tw-w-full tw-items-center tw-justify-center">
        <div class="tw-text-center">
            <div class="tw-inline-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-border/60 tw-border-t-brand tw-animate-spin" role="status" aria-label="Ładowanie">
                <span class="tw-sr-only">Ładowanie...</span>
            </div>
            <div class="tw-mt-2 tw-text-sm tw-text-muted">Ładowanie zawartości...</div>
        </div>
    </div>
`;

const RETRY_BUTTON_CLASS = 'taskello-ui-reload';

class Dynamic {

    constructor() {
        this.uiActiveRequests = {};
        this.pageNavigation = null;
        this.defaultReloadAttempts = {};
        this.defaultReloadAttemptsLimit = 5;
        this.defaultReloadAttemptsWindow = 2000;
        this.delayedReloadAttempts = {};
        this.delayedReloadRetryDelays = [300, 900];
        this.uiScrollPositions = {};

        this.bindGlobalInterop();
        this.bindDocumentEvents();
    }

    getJquery() {
        return window.jQuery || window.$ || null;
    }

    log(...args) {
        const debug = window.taskello?.debug || window.Taskello?.debug;
        debug?.log?.(...args);
    }

    hasAppShell() {
        return Boolean(
            document.querySelector('#content') &&
            document.querySelector('#topbar') &&
            document.querySelector('#sidebar')
        );
    }

    getCurrentUrl() {
        return `${window.location.pathname}${window.location.search}${window.location.hash}`;
    }

    normalizeUrl(url) {
        const parsedUrl = new URL(url, window.location.origin);
        return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
    }

    sanitizeRedirectUrl(url) {
        try {
            const parsedUrl = new URL(url, window.location.href);

            if (!['http:', 'https:'].includes(parsedUrl.protocol) || parsedUrl.origin !== window.location.origin) {
                return '/dashboard';
            }

            return url;
        } catch {
            return '/dashboard';
        }
    }

    shouldBypassAjaxNavigation(url) {
        if (!url) {
            return false;
        }

        try {
            const parsedUrl = new URL(url, window.location.origin);
            const pathname = parsedUrl.pathname;
            const bypassPrefixes = [
                '/login',
                '/logout',
                '/register',
                '/remember_password',
                '/change_remember_password',
                '/ban'
            ];

            return bypassPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(prefix + '/'));
        } catch {
            return false;
        }
    }

    isPartialTemplateUrl(url) {
        try {
            return new URL(url, window.location.origin).pathname.startsWith('/templates/');
        } catch {
            return false;
        }
    }

    shouldUseAjaxNavigation(url) {
        if (!this.hasAppShell()) {
            return false;
        }

        try {
            const parsedUrl = new URL(url, window.location.origin);

            if (parsedUrl.origin !== window.location.origin) {
                return false;
            }

            if (this.shouldBypassAjaxNavigation(parsedUrl.href)) {
                return false;
            }

            return parsedUrl.pathname.startsWith('/');
        } catch {
            return false;
        }
    }

    isFullHtmlDocument(response) {
        if (typeof response !== 'string') {
            return false;
        }

        const trimmed = response.trim().toLowerCase();
        return trimmed.startsWith('<!doctype html') || trimmed.startsWith('<html');
    }

    closeMobileSidebar() {
        const sidebar = document.querySelector('#sidebar');
        const overlay = document.querySelector('#sidebarOverlay');

        if (!sidebar || !overlay || !window.matchMedia('(max-width: 920px)').matches) {
            return;
        }

        sidebar.classList.remove('show');
        overlay.classList.remove('show');
        document.body.classList.remove('sidebar-open');

        const toggle = document.querySelector('#menuToggle');
        if (toggle) {
            toggle.setAttribute('aria-expanded', 'false');
        }
    }

    tryParseJson(response) {
        if (typeof response !== 'string') {
            this.log('Weryfikowanie odpowiedzi: json (OK)', response);
            return response;
        }

        try {
            const parsed = JSON.parse(response);
            this.log('Weryfikowanie odpowiedzi: json (OK)', parsed);
            return parsed;
        } catch {
            this.log('Weryfikowanie odpowiedzi: json (NOK)', response);
            return null;
        }
    }

    getFinalResponseUrl(xhr, fallbackUrl = null) {
        const responseUrl = xhr?.responseURL || xhr?.getResponseHeader?.('X-Response-URL') || fallbackUrl;

        if (!responseUrl) {
            return null;
        }

        try {
            return this.normalizeUrl(responseUrl);
        } catch {
            return null;
        }
    }

    uiAbortRequest(selector) {
        this.log('uiAbortRequest', selector);

        if (!this.uiActiveRequests[selector]) {
            return;
        }

        try {
            this.uiActiveRequests[selector].abort();
        } catch {
        }

        delete this.uiActiveRequests[selector];
    }

    uiRestoreScrollPosition(scrollSelector) {
        if (!scrollSelector) {
            return;
        }

        const preservedScrollTop = this.uiScrollPositions[scrollSelector];

        if (preservedScrollTop === null || preservedScrollTop === undefined) {
            return;
        }

        const applyScroll = () => {
            const scrollTarget = document.querySelector(scrollSelector);

            if (scrollTarget) {
                scrollTarget.scrollTop = preservedScrollTop;
            }
        };

        if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => {
                applyScroll();
                window.setTimeout(applyScroll, 50);
            });
        } else {
            window.setTimeout(applyScroll, 0);
            window.setTimeout(applyScroll, 50);
        }
    }

    getWindowScrollY() {
        if (typeof window.scrollY === 'number') {
            return window.scrollY;
        }

        return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    }

    uiRestoreWindowScrollPosition(scrollY) {
        if (scrollY === null || scrollY === undefined) {
            return;
        }

        const applyScroll = () => {
            const root = document.documentElement;
            const previousScrollBehavior = root?.style?.scrollBehavior || '';

            if (root?.style) {
                root.style.scrollBehavior = 'auto';
            }

            window.scrollTo({
                top: scrollY,
                left: 0,
                behavior: 'auto'
            });

            if (root?.style) {
                root.style.scrollBehavior = previousScrollBehavior;
            }
        };

        if (typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => {
                applyScroll();
                window.setTimeout(applyScroll, 50);
            });
        } else {
            window.setTimeout(applyScroll, 0);
            window.setTimeout(applyScroll, 50);
        }
    }

    defaultReloadShouldProceed(url) {
        if (!url) {
            return false;
        }

        const now = Date.now();
        const limit = this.defaultReloadAttemptsLimit || 1;
        const windowMs = this.defaultReloadAttemptsWindow || 0;

        let state = this.defaultReloadAttempts[url] || { count: 0, lastAttempt: 0 };

        if (windowMs > 0 && state.lastAttempt && now - state.lastAttempt > windowMs) {
            state = { count: 0, lastAttempt: now };
        }

        state.count += 1;
        state.lastAttempt = now;
        this.defaultReloadAttempts[url] = state;

        return state.count <= limit;
    }

    defaultReloadReset(url) {
        if (url && this.defaultReloadAttempts[url]) {
            delete this.defaultReloadAttempts[url];
        }

        if (url && this.delayedReloadAttempts[url]) {
            delete this.delayedReloadAttempts[url];
        }
    }

    scheduleDelayedReload(url) {
        if (!url) {
            return null;
        }

        const delays = this.delayedReloadRetryDelays || [];
        const currentAttempt = this.delayedReloadAttempts[url] || 0;

        if (currentAttempt >= delays.length) {
            return null;
        }

        const delay = delays[currentAttempt];
        this.delayedReloadAttempts[url] = currentAttempt + 1;
        this.log('Planowanie ponownego przeładowania widoku', url, {
            attempt: currentAttempt + 1,
            delay
        });

        return new Promise((resolve, reject) => {
            window.setTimeout(() => {
                this.uiChangePage(url, '#content', { pushState: false })
                    .then(resolve)
                    .catch(reject);
            }, delay);
        });
    }

    getResponseToast(response) {
        if (!response?.text) {
            return null;
        }

        return {
            message: response.text,
            type: response.type || 'info'
        };
    }

    showResponseToast(response) {
        const toast = this.getResponseToast(response);
        if (!toast) {
            return;
        }

        window.taskello?.showToast?.(toast.message, toast.type);
    }

    reloadVisibleTables() {
        const $ = this.getJquery();
        const $tables = $('.table-module[id]');

        if ($tables.length === 0) {
            return Promise.resolve(false);
        }

        if (!$?.fn?.ajaxTable) {
            this.log('Pominięto przeładowanie tabel - plugin ajaxTable niedostępny', $tables);
            return Promise.resolve(false);
        }

        this.log('Przeładowanie wszystkich tabel', $tables);

        return new Promise((resolve) => {
            $(document).ajaxTable('reload', () => {
                resolve(true);
            });
        });
    }

    getCurrentContentUrl() {
        return document.querySelector('#content')?.getAttribute('data-url') || this.getCurrentUrl();
    }

    shouldHandleRedirectAsReload(url) {
        if (!url || !this.shouldUseAjaxNavigation(url)) {
            return false;
        }

        return this.normalizeUrl(url) === this.getCurrentContentUrl();
    }

    reloadRedirectTarget(response, allowDefaultReload = true) {
        this.showResponseToast(response);

        const currentUrl = this.getCurrentContentUrl();
        if (!allowDefaultReload || !currentUrl) {
            return Promise.resolve({ status: 'skipped' });
        }

        return this.uiChangePage(currentUrl, '#content', {
            pushState: false,
            forceReload: true
        })
            .then(() => ({ status: 'reloaded' }))
            .catch((error) => {
                this.log('Błąd podczas przeładowania widoku po redirect', currentUrl, error);
                window.location.href = currentUrl;
                return { status: 'redirected', error };
            });
    }

    reloadCurrentView(response, allowDefaultReload = true, options = {}) {
        this.showResponseToast(response);

        const currentUrl = this.getCurrentContentUrl();
        const settings = {
            reloadContent: false,
            ...options
        };
        const tryReloadPage = () => {
            if (!allowDefaultReload || !currentUrl) {
                return Promise.resolve({ status: 'skipped' });
            }

            if (this.defaultReloadShouldProceed(currentUrl)) {
                return this.uiChangePage(currentUrl, '#content', { pushState: false })
                    .then(() => ({ status: 'reloaded' }))
                    .catch((error) => {
                        this.log('Błąd podczas automatycznego przeładowania widoku', currentUrl, error);
                        return { status: 'failed', error };
                    });
            }

            const delayedReloadPromise = this.scheduleDelayedReload(currentUrl);

            if (!delayedReloadPromise) {
                return Promise.resolve({ status: 'blocked' });
            }

            return delayedReloadPromise
                .then(() => ({ status: 'reloaded' }))
                .catch((error) => {
                    this.log('Błąd podczas opóźnionego przeładowania widoku', currentUrl, error);
                    return { status: 'failed', error };
                });
        };

        if (settings.reloadContent && currentUrl) {
            return Promise.allSettled([
                this.reloadVisibleTables(),
                tryReloadPage()
            ]).then(([tableReloadResult, pageReloadResult]) => {
                if (pageReloadResult.status === 'fulfilled' && pageReloadResult.value?.status === 'reloaded') {
                    return;
                }

                if (tableReloadResult.status === 'fulfilled' && tableReloadResult.value) {
                    return;
                }

                this.log('Pominięto domyślne przeładowanie, wykryto możliwą pętlę', currentUrl);
                window.taskello?.showToast?.('Nie udało się automatycznie odświeżyć strony. Odśwież ją ręcznie.', 'error');
            });
        }

        return this.reloadVisibleTables().then((reloadedTables) => {
            if (reloadedTables) {
                return;
            }

            return tryReloadPage().then((pageReloadResult) => {
                if (pageReloadResult?.status === 'reloaded' || pageReloadResult?.status === 'skipped') {
                    return;
                }

                this.log('Pominięto domyślne przeładowanie, wykryto możliwą pętlę', currentUrl);
                window.taskello?.showToast?.('Nie udało się automatycznie odświeżyć strony. Odśwież ją ręcznie.', 'error');
            });
        });
    }

    navigateWithToast(url, response) {
        url = this.sanitizeRedirectUrl(url);
        const toast = this.getResponseToast(response);

        if (!response?.hardRedirect && this.shouldUseAjaxNavigation(url)) {
            return this.uiChangePage(url)
                .then(() => {
                    if (toast) {
                        window.taskello?.showToast?.(toast.message, toast.type);
                    }
                });
        }

        if (toast) {
            window.taskello?.queueToast?.(toast.message, toast.type);
        }

        window.location.href = url;
        return Promise.resolve();
    }

    destroyResponseModal($element = null) {
        const $ = this.getJquery();
        if (!$?.fn?.ajaxmodal) {
            this.log('Pominięto niszczenie modala - plugin ajaxmodal niedostępny');
            return;
        }

        const $targetModal = $element?.closest?.('.modal');
        const $modal = $targetModal?.length ? $targetModal : $('.modal').last();

        this.log('Niszczenie modala', $modal);

        if (!$modal?.length) {
            this.log('Nie znaleziono modala do zniszczenia');
            window.taskello?.cleanupModalArtifacts?.();
            return;
        }

        $modal.ajaxmodal('destroy');
    }

    uiReload(selector, options = {}) {
        this.log('uiReload', selector, options);

        const $ = this.getJquery();
        if (!$) {
            return Promise.reject(new Error('jQuery is required'));
        }

        let $content = $(selector).first();
        if ($content.length === 0) {
            return Promise.reject(new Error(`Selector not found: ${selector}`));
        }

        const settings = $.extend({
            showLoader: true,
            loaderHtml: LOADER_HTML,
            preserveScrollSelector: null,
            preserveWindowScroll: false,
            allowDefaultReload: selector === '#content',
            onSuccess: null,
            onError: null
        }, options);

        const scrollSelector = settings.preserveScrollSelector;
        const scrollTarget = scrollSelector ? document.querySelector(scrollSelector) : null;
        const preservedWindowScrollY = settings.preserveWindowScroll ? this.getWindowScrollY() : null;
        if (scrollSelector && scrollTarget) {
            this.uiScrollPositions[scrollSelector] = scrollTarget.scrollTop;
        }

        let dataUrl = $content.data('url');
        if (options.url) {
            dataUrl = options.url;
            $content.attr('data-url', dataUrl);
        }

        if (!dataUrl) {
            return Promise.reject(new Error(`Brak data-url dla ${selector}`));
        }

        this.uiAbortRequest(selector);

        if (settings.showLoader) {
            $content.html(settings.loaderHtml);
        }

        return new Promise((resolve, reject) => {
            this.uiActiveRequests[selector] = $.ajax({
                url: dataUrl,
                type: 'GET',
                dataType: 'html',
                success: (response, textStatus, xhr) => {
                    delete this.uiActiveRequests[selector];

                    const contentType = xhr.getResponseHeader('Content-Type') || '';
                    const finalUrl = this.getFinalResponseUrl(xhr, dataUrl);
                    const parsedJson = contentType.includes('application/json')
                        ? this.tryParseJson(response)
                        : null;

                    if (parsedJson !== null) {
                        Promise.resolve(this.uiAjaxResponseProcess(parsedJson, $content, {
                            allowDefaultReload: settings.allowDefaultReload
                        }))
                            .then((processedResponse) => {
                                resolve(processedResponse ?? parsedJson);
                            })
                            .catch(reject);
                        return;
                    }

                    if (
                        selector === '#content' &&
                        finalUrl &&
                        finalUrl !== this.normalizeUrl(dataUrl) &&
                        this.shouldBypassAjaxNavigation(finalUrl)
                    ) {
                        window.location.href = finalUrl;
                        resolve(response);
                        return;
                    }

                    if (selector === '#content' && this.isFullHtmlDocument(response)) {
                        window.location.href = finalUrl || dataUrl;
                        resolve(response);
                        return;
                    }

                    const sourceNode = $content.get(0);
                    const replacementTag = sourceNode?.tagName?.toLowerCase() || 'div';
                    const $replacement = $(`<${replacementTag}>`);

                    if (sourceNode) {
                        Array.from(sourceNode.attributes).forEach((attr) => {
                            $replacement.attr(attr.name, attr.value);
                        });
                    }

                    $replacement.html(response);
                    $content.replaceWith($replacement);
                    $content = $replacement;

                    this.uiRestoreScrollPosition(scrollSelector);
                    this.uiRestoreWindowScrollPosition(preservedWindowScrollY);
                    $content.attr('data-url', dataUrl);
                    this.defaultReloadReset(dataUrl);

                    if (typeof settings.onSuccess === 'function') {
                        settings.onSuccess.call($content.get(0), response);
                    }

                    resolve(response);
                },
                error: (xhr, status, error) => {
                    delete this.uiActiveRequests[selector];

                    if (status === 'abort') {
                        resolve();
                        return;
                    }

                    const errorHtml = `
                        <div class="tw-rounded-2xl tw-border tw-border-danger/20 tw-bg-danger/5 tw-p-4 tw-text-sm tw-text-text" role="alert">
                            <div class="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
                                <div class="tw-flex tw-items-start tw-gap-3">
                                    <span class="tw-mt-0.5 tw-inline-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-text-danger">
                                        <i class="bi bi-exclamation-triangle tw-text-base"></i>
                                    </span>
                                    <div class="tw-min-w-0">
                                        <div class="tw-font-medium tw-text-text">Nie udało się załadować zawartości</div>
                                        <div class="tw-mt-1 tw-leading-6 tw-text-muted">Wystąpił błąd podczas ładowania zawartości. Spróbuj ponownie.</div>
                                    </div>
                                </div>
                                <button class="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-border tw-border-danger/20 tw-bg-surface tw-px-3.5 tw-py-2 tw-text-sm tw-font-medium tw-text-danger tw-transition hover:tw-bg-danger/5 ${RETRY_BUTTON_CLASS}" data-selector="${selector}">
                                    <i class="bi bi-arrow-clockwise"></i>
                                    <span>Spróbuj ponownie</span>
                                </button>
                            </div>
                        </div>
                    `;

                    $content.html(errorHtml);

                    if (typeof settings.onError === 'function') {
                        settings.onError.call($content.get(0), xhr, status, error);
                    }

                    reject(new Error(error || status || 'Request failed'));
                }
            });
        });
    }

    async uiChangePage(url, selector = '#content', options = {}) {
        this.log('uiChangePage', url, selector, options);

        if (!this.shouldUseAjaxNavigation(url)) {
            window.location.href = url;
            return Promise.resolve();
        }

        if (selector === '#content' && this.isPartialTemplateUrl(url)) {
            return Promise.resolve({ status: 'cancelled' });
        }

        const normalizedUrl = this.normalizeUrl(url);
        const pushState = options.pushState !== false;
        const shouldConfirmUnsavedChanges = options.confirmUnsavedChanges !== false
            && selector === '#content'
            && normalizedUrl !== this.getCurrentContentUrl();

        if (
            shouldConfirmUnsavedChanges
            && !(await (window.Taskello?.unsavedChanges?.confirmNavigation?.() ?? true))
        ) {
            return Promise.resolve({ status: 'cancelled' });
        }

        const preserveWindowScroll = options.preserveWindowScroll === true
            || (
                options.preserveWindowScroll !== false
                && selector === '#content'
                && normalizedUrl === this.getCurrentContentUrl()
            );

        if (
            options.forceReload !== true &&
            this.pageNavigation &&
            this.pageNavigation.url === normalizedUrl &&
            this.pageNavigation.selector === selector
        ) {
            return this.pageNavigation.promise;
        }

        Object.keys(this.uiActiveRequests).forEach((key) => {
            this.uiAbortRequest(key);
        });

        if (pushState && normalizedUrl !== this.getCurrentUrl()) {
            window.history.pushState({ path: normalizedUrl }, '', normalizedUrl);
        }

        this.closeMobileSidebar();
        document.querySelectorAll('[role="tooltip"]').forEach((tooltip) => tooltip.remove());

        const contentPromise = this.uiReload(selector, {
            url: normalizedUrl,
            preserveWindowScroll
        });
        const topbarPromise = this.uiReload('#topbar', { showLoader: false });
        const sidebarPromise = contentPromise.then(() => this.uiReload('#sidebar', {
            showLoader: false,
            preserveScrollSelector: '#sidebar'
        }));

        this.reloadVisibleTables();

        const navigationPromise = Promise.all([contentPromise, topbarPromise, sidebarPromise])
            .finally(() => {
                if (this.pageNavigation?.promise === navigationPromise) {
                    this.pageNavigation = null;
                }
            });

        this.pageNavigation = {
            url: normalizedUrl,
            selector,
            promise: navigationPromise
        };

        return navigationPromise;
    }

    uiAjaxResponseProcess(response, $element = null, options = {}) {
        this.log('uiAjaxResponseProcess', response, $element, options);

        const { allowDefaultReload = true } = options;

        if (!response || typeof response !== 'object') {
            return Promise.resolve();
        }

        if (response.type === 'redirect' && response.url) {
            if (response.modalDestroy) {
                this.destroyResponseModal($element);
            }

            if (!response.hardRedirect && this.shouldHandleRedirectAsReload(response.url)) {
                return this.reloadRedirectTarget(response, allowDefaultReload);
            }

            return this.navigateWithToast(response.url, response);
        }

        if (response.modalDestroy) {
            this.destroyResponseModal($element);
        }

        if (response.redirect) {
            if (this.shouldHandleRedirectAsReload(response.redirect)) {
                return this.reloadRedirectTarget(response, allowDefaultReload);
            }

            return this.navigateWithToast(response.redirect, response);
        }

        return this.reloadCurrentView(response, allowDefaultReload);
    }

    processResponse(response, $element = null, options = {}) {
        this.log('processResponse', response, $element, options);
        return this.uiAjaxResponseProcess(response, $element, options);
    }

    shouldInterceptLink(link, event) {
        if (!link || !this.hasAppShell()) {
            return false;
        }

        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return false;
        }

        if (link.target && link.target !== '_self') {
            return false;
        }

        if (link.hasAttribute('download') || link.classList.contains('disable-ajax') || link.classList.contains('modallink')) {
            return false;
        }

        if (link.closest('[data-modal-dialog]')) {
            return false;
        }

        const href = link.getAttribute('href');
        if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) {
            return false;
        }

        try {
            const parsedUrl = new URL(href, window.location.origin);
            return parsedUrl.origin === window.location.origin && parsedUrl.pathname.startsWith('/');
        } catch {
            return false;
        }
    }

    bindDocumentEvents() {
        document.addEventListener('click', (event) => {
            const retryButton = event.target.closest(`.${RETRY_BUTTON_CLASS}`);
            if (retryButton) {
                event.preventDefault();
                const selector = retryButton.getAttribute('data-selector');
                if (selector) {
                    this.uiReload(selector);
                }
                return;
            }

            const link = event.target.closest('a');
            if (!this.shouldInterceptLink(link, event)) {
                return;
            }

            event.preventDefault();
            this.uiChangePage(link.href).catch((error) => {
                this.log('Błąd podczas dynamicznej zmiany strony', error);
                window.location.href = link.href;
            });
        });

        window.addEventListener('popstate', () => {
            if (!this.hasAppShell()) {
                return;
            }

            if (this.isPartialTemplateUrl(this.getCurrentUrl())) {
                window.location.replace('/dashboard');
                return;
            }

            const previousContentUrl = this.getCurrentContentUrl();

            this.uiChangePage(this.getCurrentUrl(), '#content', { pushState: false }).then((result) => {
                if (result?.status === 'cancelled' && previousContentUrl) {
                    window.history.pushState({ path: previousContentUrl }, '', previousContentUrl);
                }
            }).catch((error) => {
                this.log('Błąd podczas obsługi popstate', error);
                window.location.href = this.getCurrentUrl();
            });
        });
    }

    bindGlobalInterop() {
        const jquery = this.getJquery();
        if (jquery) {
            jquery.uiChangePage = (url, selector = '#content', options = {}) => this.uiChangePage(url, selector, options);
            jquery.uiReload = (selector, options = {}) => this.uiReload(selector, options);
        }

        window.taskello = window.taskello || {};
        window.taskello.uiChangePage = (url, selector = '#content', options = {}) => this.uiChangePage(url, selector, options);
        window.taskello.uiReload = (selector, options = {}) => this.uiReload(selector, options);
        window.taskello.uiAjaxResponseProcess = (response, $element = null, options = {}) => this.uiAjaxResponseProcess(response, $element, options);
        window.taskello.defaultReloadShouldProceed = (url) => this.defaultReloadShouldProceed(url);
        window.taskello.defaultReloadReset = (url) => this.defaultReloadReset(url);
    }
}

window.dynamic = new Dynamic();
